/**
 * 组件：高级弹窗组件
 * 功能：省去维护 visible 和 confirmLoading
 */

import * as React from 'react'
import { Modal, Button, Form } from 'antd'
import type { ButtonProps } from 'antd/lib/button'
import type { ModalProps } from 'antd/lib/modal'
import type { FormInstance, FormProps } from 'antd/lib/form'
import { useTranslation } from '@/libs/next-lang'

const { useState } = React
const noop = () => {}

interface UseModalProps extends ModalProps {
  onOk?: (p?: any) => any
  onCancel?: (p?: any) => any
  okButtonProps?: ButtonProps & { visible?: boolean }
  cancelButtonProps?: ButtonProps & { visible?: boolean }
  footer?: any
  cancelText?: string
  okText?: string
}

interface ChildrenProps {
  form: FormInstance
  hideModal: () => void
  showParams?: any
}

interface ModalWrapInterface {
  style?: React.CSSProperties
  modalProps: UseModalProps
  formProps?: FormProps
  children: (obj: ChildrenProps) => React.ReactNode
}

interface ModalWrapResult {
  render: () => React.ReactNode
  showModal: (r?: any) => void
  visible: boolean
  form: FormInstance
  hideModal: () => void
  isValueChange?: boolean
  showParams?: Record<string, any>
}

export default function useModalWrap(props: ModalWrapInterface): ModalWrapResult {
  const { style, modalProps, formProps, children } = props
  const [form] = Form.useForm()
  const [isValueChange, setIsValueChange] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const { cancelButtonProps, okButtonProps, footer } = modalProps
  // 保存showModal 传入的参数，注入到children 上
  const [showParams, setShowParams] = useState<any>(noop())

  const { t } = useTranslation(['components'])

  const showModal = (record?: any) => {
    if (record) {
      setShowParams(record)
    }
    setVisible(true)
  }
  const hideModal = () => {
    setVisible(false)
  }
  const closeModal = () => {
    setVisible(false)
    setIsValueChange(false)
    form.resetFields()
  }

  const onCancel = (event: any) => {
    const isMask: boolean = event.target?.className?.includes?.('-modal-wrap') || false
    if (modalProps.onCancel) {
      modalProps.onCancel(() => {
        closeModal()
      })
    } else if (isValueChange && isMask) {
      Modal.confirm({
        title: t('components:t83'),
        content: t('components:t84'),
        okText: t('components:t59'),
        cancelText: t('components:t85'),
        onOk: () => {
          closeModal()
        },
      })
    } else {
      closeModal()
    }
  }
  const onOk = () => {
    form
      .validateFields()
      .then(values => {
        if (modalProps.onOk) {
          setConfirmLoading(true)
          Promise.resolve(modalProps.onOk(values))
            .then(v => {
              if (v === false) {
                return
              }
              form.resetFields()
              closeModal()
            })
            .finally(() => setConfirmLoading(false))
        }
      })
      .catch(({ errorFields }) => {
        const firstError = (errorFields || [])[0] || {}
        form.scrollToField(firstError.name, {
          behavior: actions => {
            return actions.forEach(({ el, top, left }: any) => {
              // eslint-disable-next-line no-param-reassign
              el.style.scrollBehavior = 'smooth'
              // eslint-disable-next-line no-param-reassign
              el.scrollTop = top - 50
              // eslint-disable-next-line no-param-reassign
              el.scrollLeft = left
            })
          },
        })
      })
  }

  const renderFooter = () => {
    if (footer === null) {
      return null
    }
    if (footer) {
      return footer
    }
    const { visible: cancelButtonVisible, ...restCancelButtonProps } = cancelButtonProps || {}
    const { visible: okButtonVisible, ...restOkButtonProps } = okButtonProps || {}
    return (
      <div>
        {cancelButtonVisible !== false && (
          <Button key="back" {...restCancelButtonProps} onClick={onCancel}>
            {modalProps.cancelText || t('components:t85')}
          </Button>
        )}
        {okButtonVisible !== false && (
          <Button
            key="submit"
            type="primary"
            {...restOkButtonProps}
            loading={confirmLoading}
            onClick={onOk}
          >
            {modalProps.okText || t('components:t59')}
          </Button>
        )}
      </div>
    )
  }

  const modalOpt: ModalProps = {
    centered: true,
    destroyOnClose: true,
    // maskClosable: false,
    ...modalProps,
    open: visible,
    onCancel,
    footer: renderFooter(),
  }
  return {
    render: () => (
      <div style={{ display: 'inline-block', ...style }}>
        <Form
          form={form}
          labelAlign="left"
          onValuesChange={() => {
            setIsValueChange(true)
          }}
          {...formProps}
        >
          <Modal {...modalOpt}>{children({ form, hideModal, showParams })}</Modal>
        </Form>
      </div>
    ),
    showModal,
    hideModal,
    visible,
    form,
    isValueChange,
    showParams,
  }
}
